import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchSection from "../SearchSection/SearchSection.tsx";

interface Column {
  id: string;
  label: string;
  searchable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
}

interface Data {
  [key: string]: any;
}

interface CoreTableProps {
  columns: Column[];
  data: Data[];
  onUpdate?: (id: number) => void;
  onDelete?: (id: number) => void;
  label1?: string;
  label2?: string;
  hasMore?: boolean;
  loadMore?: () => void;
  totalCount?: number;
  infiniteScroll?: boolean;
  onSearch?: (field: string, value: string) => void;
  onClearFilter?: (field: string) => void;
  showSearch?: boolean;
  activeFilters?: {
    [key: string]: string | null;
  };
}

const getNestedProperty = (obj: any, path: string) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj) || "N/A";
};

const CoreTable: React.FC<CoreTableProps> = ({
  columns,
  data,
  onUpdate,
  onDelete,
  label1,
  label2,
  hasMore = false,
  loadMore,
  totalCount,
  infiniteScroll = false,
  onSearch,
  onClearFilter,
  showSearch = false,
  activeFilters = {},
}) => {
  const [searchField, setSearchField] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  const LoadingIndicator = () => (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <CircularProgress size={30} />
    </div>
  );

  const handleSearch = () => {
    if (onSearch && searchField && searchValue) {
      onSearch(searchField, searchValue);
      // setSearchField("");
      // setSearchValue("");
    }
  };

  const handleReset = () => {
    // setSearchField("");
    setSearchValue("");
    // Clear all active filters
    if (onClearFilter) {
      Object.keys(activeFilters).forEach(field => {
        if (activeFilters[field]) {
          onClearFilter(field);
        }
      });
    }
  };
  //   const searchableColumns = columns.filter(col => col.searchable !== false);

  //   return (
  //     <Box sx={{ mb: 2, mt: 1, display: "flex", gap: 2, alignItems: "center" }}>
  //       <FormControl sx={{ minWidth: 200 }}>
  //         <InputLabel>Search Field</InputLabel>
  //         <Select
  //           value={searchField}
  //           label="Search Field"
  //           onChange={(e) => setSearchField(e.target.value)}
  //           size="small"
  //         >
  //           {searchableColumns.map((column) => (
  //             <MenuItem key={column.id} value={column.id}>
  //               {column.label}
  //             </MenuItem>
  //           ))}
  //         </Select>
  //       </FormControl>

  //       <TextField
  //         label="Search Value"
  //         value={searchValue}
  //         onChange={(e) => setSearchValue(e.target.value)}
  //         size="small"
  //         sx={{ minWidth: 200 }}
  //         autoFocus
  //       />

  //       <Button
  //         variant="contained"
  //         onClick={handleSearch}
  //         disabled={!searchField || !searchValue}
  //         size="small"
  //       >
  //         Search
  //       </Button>

  //       <Button
  //         variant="outlined"
  //         onClick={handleReset}
  //         size="small"
  //       >
  //         Reset
  //       </Button>
  //     </Box>
  //   );
  // };

  const TableContent = () => (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((column) => {
            const activeFilter = activeFilters[column.id];

            return (
              <TableCell
                key={column.id}
                sx={{
                  fontWeight: "bold",
                  minWidth: "150px",
                  borderRight: "1px solid #e0e0e0",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#fff",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{column.label}</span>
                  {activeFilter && (
                    <Chip
                      size="small"
                      label={activeFilter}
                      onDelete={() => onClearFilter && onClearFilter(column.id)}
                      deleteIcon={<CloseIcon style={{ fontSize: '16px' }} />}
                      sx={{
                        height: '20px',
                        '& .MuiChip-label': {
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
            );
          })}
          <TableCell
            sx={{
              fontWeight: "bold",
              borderRight: "1px solid #e0e0e0",
              position: "sticky",
              right: 0,
              top: 0,
              zIndex: 3,
              backgroundColor: "#fff",
            }}
          >
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.map((row) => (
          <TableRow key={row.id}>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                  borderRight: "1px solid #e0e0e0",
                  textAlign: "center",
                  minWidth: "150px",
                }}
              >
                {column.render  // Add this conditional rendering
                  ? column.render(getNestedProperty(row, column.id), row)
                  : getNestedProperty(row, column.id)
                }
              </TableCell>
            ))}
            <TableCell
              sx={{
                borderBottom: "1px solid #e0e0e0",
                borderRight: "1px solid #e0e0e0",
                textAlign: "center",
                position: "sticky",
                right: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {typeof onUpdate === 'function' ? <Button
                  variant="contained"
                  style={{ backgroundColor: "#4a5568", color: "#fff" }}
                  size="small"
                  onClick={() => onUpdate(row.id)}
                >
                  {label1 || 'Update'}
                </Button> : null
                }
                {typeof onDelete === 'function' ? <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => onDelete(row.id)}
                >
                  {label2 || 'Delete'}
                </Button> : null
                }
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const containerStyle = {
    width: "100%",
    height: "calc(100vh - 88px)",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden" as const,
    padding: "16px",
    boxSizing: "border-box" as const,
  };

  const tableContainerStyle = {
    flex: 1,
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
    position: "relative" as const,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden" as const,
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    minHeight: "400px",
  };

  const tableWrapperStyle = {
    flex: 1,
    overflow: "auto" as const,
    position: "relative" as const,
  };

  const paginationStyle = {
    position: "sticky" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#f5f5f5",
    padding: "10px",
    textAlign: "center" as const,
    borderTop: "1px solid #e0e0e0",
    zIndex: 10,
    width: "100%",
  };

  return (
    <div style={containerStyle}>
      {showSearch && <SearchSection
        columns={columns}
        searchField={searchField}
        searchValue={searchValue}
        setSearchField={setSearchField}
        setSearchValue={setSearchValue}
        handleSearch={handleSearch}
        handleReset={handleReset}
      />}

      {data?.length > 0 && infiniteScroll ? (
        <div
          id="scrollableDiv"
          style={{
            ...tableContainerStyle,
            height: showSearch ? 'calc(100% - 70px)' : '100%',
            overflow: 'auto'
          }}
        >
          <InfiniteScroll
            dataLength={data.length}
            next={loadMore!}
            hasMore={hasMore}
            loader={<LoadingIndicator />}
            scrollableTarget="scrollableDiv"
            style={{ overflow: "visible" }}
            scrollThreshold={0.8}
            endMessage={
              <p style={{ textAlign: 'center', padding: '10px' }}>
                {totalCount && data.length >= totalCount ? 'No more data to load' : ''}
              </p>
            }
          >
            <TableContainer>
              <TableContent />
            </TableContainer>
          </InfiniteScroll>
          {totalCount && (
            <div style={paginationStyle}>
              Showing {data.length} of {totalCount} entries
            </div>
          )}
        </div>
      ) : (
        <div style={tableContainerStyle}>
          <div style={tableWrapperStyle}>
            <TableContainer
              component={Paper}
              sx={{
                height: '100%',
                overflow: "auto",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fff",
                borderRadius: "4px",
                boxShadow: "none",
              }}
            >
              <TableContent />
            </TableContainer>
          </div>
          {totalCount && (
            <div style={paginationStyle}>
              Showing {data.length} of {totalCount} entries
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoreTable;